import TemplateSurveyState from 'containers/SurveyPage/TemplateSurveyState';
import { find, get, forEach, cloneDeep } from 'lodash';

import { mapQuestionsAndGroupsAsNodes } from 'containers/QuestionnairePage/utils';
import { pipingRegEx } from 'components/FormattedInput/helpers';
import { Matcher } from 'utils/adl-services';
import {
  SURVEY_TYPE_PEEQ,
  SURVEY_TYPE_DISTRIBUTION_LINK,
} from 'containers/Survey/constants';

import { noneOfTheAboveAnswerSelector } from 'containers/QuestionnairePage/GenericQuestion/SingleMultipleQuestion/utils';

import { END_OF_SURVEY } from 'components/LogicPathPreview/constants';
import { FAILURE, REASON_SCREENOUT } from 'components/LogicOutcome/constants';

import {
  screeningQuestionToNormal,
  addScreeningQuestionWithScreeningQL,
  repointCarryForwardFromScreening,
  shouldUseScreeningQl,
  shouldConvertScreeningToQlQuestions,
  applyScreeningQuestionContentUpdate,
  finalizeConvertedScreeningPlacement,
} from '../utils';

const cpgStoreChoiceDriversGenerator = ({
  template,
  variables,
  surveyType,
  peeqCategoryType,
}) => {
  // pass the fetchedXXXX options only
  // if you intend to use the relevant template as-is

  // extract and prepare user variables
  const defaultRecurrence = get(
    find(variables, ['name', 'defaultRecurrence']),
    'value',
    undefined,
  );

  const { screeningQuestions } = mapQuestionsAndGroupsAsNodes(
    template.questionnaireTemplate,
  );

  const Survey = new TemplateSurveyState({
    fetchedQuestionnaire: {
      ...template.questionnaireTemplate,
      isPeeq: surveyType === SURVEY_TYPE_PEEQ,
      isDistributionLink: surveyType === SURVEY_TYPE_DISTRIBUTION_LINK,
    },
    fetchedTargeting:
      surveyType === SURVEY_TYPE_PEEQ
        ? template.peeqTargetingTemplate
        : template.regularTargetingTemplate,
  });

  if (shouldConvertScreeningToQlQuestions({ surveyType, peeqCategoryType })) {
    // add questions
    // remove from question:isScreening
    // and from answers: screenerType, screening,
    const screeningQuestion = screeningQuestions[0];
    const screenerQuestion = screeningQuestionToNormal(screeningQuestion);
    Survey.questionnaire.addQuestion(screenerQuestion);

    repointCarryForwardFromScreening(
      Survey,
      screenerQuestion.refId,
      screeningQuestion.refId,
    );

    const replaceRecall = (value) =>
      value.replace(pipingRegEx, `{{refId:${screenerQuestion.refId}}}`);

    forEach(
      Survey.questionnaire.state.surveyDetailsExtended.questions,
      (question) => {
        const updatedQuestion = {
          ...question,
          prompt: replaceRecall(question.prompt),
          htmlPrompt: replaceRecall(question.htmlPrompt),
        };
        Survey.questionnaire.updateQuestion(updatedQuestion);
      },
    );
    Survey.questionnaire.updateQuestion(screenerQuestion);

    // set ADL to screenout
    const noneOfAboveStatementId = Survey.questionnaire.linkAnswerToQuestion({
      question: screenerQuestion,
      answerRefId: noneOfTheAboveAnswerSelector(screenerQuestion.answers).refId,
      targetQuestion: { refId: END_OF_SURVEY },
      context: { questionId: screenerQuestion.refId },
      matcher: Matcher.Equal,
    });
    Survey.questionnaire.updateStatementOutcome({
      nodeRefId: screenerQuestion.refId,
      statementId: noneOfAboveStatementId,
      outcome: {
        params: { id: END_OF_SURVEY },
        type: FAILURE,
        reason: REASON_SCREENOUT,
      },
    });

    finalizeConvertedScreeningPlacement(Survey, [
      {
        originalRefId: screeningQuestion.refId,
        convertedQuestion: screenerQuestion,
      },
    ]);
  } else if (shouldUseScreeningQl({ surveyType, peeqCategoryType })) {
    // add questions
    // remove from question:isScreening
    // and from answers: screenerType, screening,
    const screeningQuestionClone = cloneDeep(screeningQuestions[0]);

    const newQLScreener = addScreeningQuestionWithScreeningQL(
      Survey,
      screeningQuestionClone,
    );
    applyScreeningQuestionContentUpdate(Survey, newQLScreener, {
      surveyType,
      peeqCategoryType,
    });

    repointCarryForwardFromScreening(
      Survey,
      newQLScreener.refId,
      screeningQuestionClone.refId,
    );

    const replaceRecall = (value) =>
      value.replace(pipingRegEx, `{{refId:${newQLScreener.refId}}}`);

    forEach(
      Survey.questionnaire.state.surveyDetailsExtended.questions,
      (question) => {
        const updatedQuestion = {
          ...question,
          prompt: replaceRecall(question.prompt),
          htmlPrompt: replaceRecall(question.htmlPrompt),
        };
        Survey.questionnaire.updateQuestion(updatedQuestion);
      },
    );

    applyScreeningQuestionContentUpdate(Survey, newQLScreener, {
      surveyType,
      peeqCategoryType,
    });
    Survey.questionnaire.removeQuestion(screeningQuestionClone);
    Survey.targeting.removeAllScreeningQuestion();
  }

  return { state: Survey.state, defaultRecurrence };
};

export default cpgStoreChoiceDriversGenerator;
